import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { Lightning, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-sm text-zinc-500 mb-4">This reset link is invalid or has expired.</div>
          <Link to="/forgot-password" className="text-zinc-950 underline underline-offset-4">Request a new one</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-body flex items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-12">
          <Lightning weight="fill" size={22} className="text-[#FF2A2A]" />
          <span className="font-heading font-black text-xl tracking-tight">RECRUITFLOW</span>
        </div>

        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
          Set a new password
        </div>
        <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl mb-10 leading-[1.05]">
          Almost there.
        </h1>

        {done ? (
          <div className="border border-emerald-500 bg-emerald-50 text-emerald-900 text-sm px-4 py-4 rounded-sm flex items-start gap-3" data-testid="reset-done">
            <CheckCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">Password updated</div>
              <div className="text-xs">Redirecting you to sign in…</div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                New password
              </label>
              <input
                data-testid="reset-password-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
                placeholder="At least 8 characters"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Confirm password
              </label>
              <input
                data-testid="reset-confirm-input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
                placeholder="Retype the same password"
              />
            </div>

            {error && (
              <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-4 py-3 rounded-sm" data-testid="reset-error">
                {error}
              </div>
            )}

            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-zinc-950 text-white py-3.5 rounded-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {submitting ? "Updating..." : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
